// models/Profile.js 
import { User } from './User';

import { Enterprise } from './Enterprise';
import { Subscription } from './Subscription';
import { Usage } from './Usage';
import { ReportTypeFavorites } from './ReportTypeFavorites';

import { Macro } from './Macro';
import { Tutorials } from './Tutorials';

export class Profile {
  constructor(data = {}) { 
      this._initializeFromFlattenedData(data);
  }

  _initializeFromFlattenedData(data) {
    // Create User model from flattened data
    this.user = new User({
      id: data.id || data.user.id,
      email: data.email || data.user.email,
      emailVerified: data.emailVerified || data.user.emailVerified || false,
      emailVerifiedAt: data.emailVerifiedAt || data.user.emailVerifiedAt || null,
      phone: data.phone || data.user.phone || null,
      firstName: data.firstName || data.user.firstName,
      lastName: data.lastName || data.user.lastName,
      title: data.title || data.user.title,
      specialty: data.specialty || data.user.specialty,
      languagePreference: data.languagePreference || data.user.languagePreference || 'français',
      dictationLanguage: data.dictationLanguage || data.user.dictationLanguage || 'fr',
      isAdmin: data?.isAdmin || data.user?.isAdmin || false,
      isEnterpriseAdmin: data?.isEnterpriseAdmin || data.user?.isEnterpriseAdmin || false,
      createdAt: data.createdAt || data.user.createdAt,
    });
    
    // Handle enterprise data
    this.enterprises = (data.enterprises || []).map(e => new Enterprise(e));
    

    // Initialize subscription
    this.subscription = new Subscription(data.subscription || {});

    this.usage = new Usage({});
    
    // Handle reportTypeFavorites - pass the entire object for new format
    this.reportTypeFavorites = new ReportTypeFavorites(data.reportTypeFavorites);
  
    
    // Macros
    this.macros = Array.isArray(data.macros) ? data.macros.map(m=>new Macro(m)) : [];
    
    // Additional data from flattened response
    this.tutorials = new Tutorials(data.tutorials || {});
    this.quickUsage = data.quickUsage || null;
    this.emails = data.emails || [];
    this.expiresAt = data.expiresAt || null;
  }


  // Convenience getters for commonly accessed properties
  get id() { return this.user.id; }
  get email() { return this.user.email; }
  get emailVerified() { return this.user.emailVerified; }
  get emailVerifiedAt() { return this.user.emailVerifiedAt; }
  get phone() { return this.user.phone; }
  get fullName() { return this.user.firstName + ' ' + this.user.lastName; }
  get fullNameAndTitle() { return (this.user.title ? this.user.title + '. ' : '') + this.user.firstName + ' ' + this.user.lastName; }
  get lastNameAndTitle() { return (this.user.title ? this.user.title + '. ' : '') + this.user.lastName; }
  get firstName() { return this.user.firstName; }
  get lastName() { return this.user.lastName; }
  get title() { return this.user.title; }
  get specialty() { return this.user.specialty; }
  get isAdmin() { return this.user.isAdmin; }
  get isEnterpriseAdmin() { return this.user.isEnterpriseAdmin; }
  get createdAt() { return this.user.createdAt; }
  
  get enterpriseNames() { return this.enterprises.map(e => e.shortName); }

  get languagePreference() { return this.user.languagePreference; }
  get dictationLanguage() { return this.user.dictationLanguage; }
  
  get isSubscriptionActive() { return this.subscription.status === 'active'; }
  get planType() { return this.subscription.planType; }
  get isGold() { return this.subscription.planType === 'gold'; }
  get isEnterprise() { return this.subscription.planType === 'enterprise'; }
  get isWood() { return this.subscription.planType === 'wood'; }
  get isSilver() { return this.subscription.planType === 'silver'; }
  get isGoldOrEnterprise() { return this.isGold || this.isEnterprise; }
  get isWoodOrSilver() { return this.isWood || this.isSilver; }
  get isSilverOrGoldOrEnterprise() { return this.isSilver || this.isGold || this.isEnterprise; }

  get subscriptionStatus() { return this.subscription.status; }

  get macrosCount() { return this.macros.length; }
  
  // Helper methods
  hasActiveSubscription() {
    return this.subscription.status === 'active';
  }

  isWithinUsageLimit(usageType) {
    const current = this.usage.daily[usageType] || 0;
    const limitKey = `maxDaily${usageType.charAt(0).toUpperCase() + usageType.slice(1)}`;
    const limit = this.usage.limits[limitKey];
    return !limit || current < limit;
  }


  // Convert to plain object for storage
  toJSON() {
    return {
      user: this.user,
      enterprises: this.enterprises,
      subscription: this.subscription,
      usage: this.usage,
      reportTypeFavorites: this.reportTypeFavorites.toJSON(),
      macros: this.macros.map(m=>m.toJSON()),
      tutorials: this.tutorials.toJSON(),
      quickUsage: this.quickUsage,
      emails: this.emails,
      expiresAt: this.expiresAt
    };
  }

  // Create from stored JSON
  static fromJSON(json) {
    return new Profile(json);
  }
}